import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MODULES, getVisibleModules } from '../config/modules';
import CommandPalette from '../components/CommandPalette';
import NotificationCenter from '../components/NotificationCenter';
import {
  ShoppingCart, DollarSign, TrendingUp, Building2, Loader2, Database,
  BarChart3, Trophy, Flame, Clock, Sun, Moon, LogOut, ChefHat, Bell,
  Package, AlertTriangle, XCircle, ArrowRight, TrendingDown, Tag, Zap
} from 'lucide-react';
import { PieChart as PieIcon } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';
import OnboardingWizard from '../components/OnboardingWizard';
import AIWelcomeMessage from '../components/AIWelcomeMessage';

const COLORS = ['#D97706', '#2563EB', '#10B981', '#EF4444', '#7C3AED', '#EC4899', '#0891B2', '#F59E0B'];

export default function DashboardPage() {
  const { user, activeBranch, setActiveBranch, authHeaders, API, seedData, branches, isDemo, logout, token, businessType, hasPermission } = useAuth();
  const { t } = useTranslation();
  const { lang, toggleLanguage, dir } = useLanguage();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [advanced, setAdvanced] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [alertCount, setAlertCount] = useState(0);
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [smartNotifs, setSmartNotifs] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(() => {
    const needs = localStorage.getItem('zouqai_needs_onboarding');
    return needs === 'true';
  });

  const toggleTheme = () => { const isDark = document.documentElement.classList.toggle('dark'); setDark(isDark); localStorage.setItem('zouqai_theme', isDark ? 'dark' : 'light'); };
  const handleLogout = () => { logout(); navigate('/'); };

  useEffect(() => { fetchData(); }, [activeBranch]);

  useEffect(() => {
    if (!activeBranch?.id || !token) return;
    const fetchAlerts = async () => {
      try {
        const { data } = await axios.get(`${API}/accounting/alerts?branch_id=${activeBranch.id}`, authHeaders());
        const dismissed = JSON.parse(localStorage.getItem('zouqai_dismissed_alerts') || '[]');
        setAlertCount((data.alerts || []).filter(a => !dismissed.includes(a.id)).length);
      } catch { /* silent */ }
    };
    const fetchInventoryAlerts = async () => {
      try {
        const { data } = await axios.get(`${API}/inventory/alerts?branch_id=${activeBranch.id}`, authHeaders());
        setInventoryAlerts((data.alerts || []).slice(0, 5));
      } catch { /* silent */ }
    };
    fetchAlerts();
    fetchInventoryAlerts();
    fetchSmartNotifs();
  }, [activeBranch, token, API, authHeaders]);

  const fetchSmartNotifs = async () => {
    if (!activeBranch?.id || !token) return;
    try {
      const { data } = await axios.get(`${API}/notifications/smart-summary?branch_id=${activeBranch.id}`, authHeaders());
      setSmartNotifs(data);
    } catch { /* silent */ }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = activeBranch ? `?branch_id=${activeBranch.id}` : '';
      const [analyticsRes, advancedRes, ordersRes] = await Promise.all([
        axios.get(`${API}/analytics${params}`, authHeaders()),
        axios.get(`${API}/analytics/advanced${params}`, authHeaders()),
        axios.get(`${API}/orders${params}`, authHeaders()).catch(() => ({ data: [] })),
      ]);
      setAnalytics(analyticsRes.data);
      setAdvanced(advancedRes.data);
      setRecentOrders((ordersRes.data || []).slice(0, 5));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try { await seedData(); toast.success(t('dashboard.seedSuccess')); fetchData(); }
    catch { toast.error(t('dashboard.seedFailed')); }
    finally { setSeeding(false); }
  };

  const visibleModules = getVisibleModules(businessType, hasPermission);
  const STATUS_LABELS = { pending: t('dashboard.statusPending'), confirmed: t('dashboard.statusConfirmed'), preparing: t('dashboard.statusPreparing'), ready: t('dashboard.statusReady'), out_for_delivery: t('dashboard.statusOutForDelivery'), delivered: t('dashboard.statusDelivered'), cancelled: t('dashboard.statusCancelled') };

  const moduleCards = visibleModules.map(mod => {
    let stat = '';
    if (mod.key === 'pos' && analytics) stat = `${analytics.total_orders || 0} ${t('dashboard.totalOrders')}`;
    if (mod.key === 'finance' && analytics) stat = `${Number(analytics.total_revenue || 0).toLocaleString()} ${t('common.sar')}`;
    if (mod.key === 'operations' && analytics) stat = `${analytics.total_orders || 0} ${t('nav.orders')}`;
    return { ...mod, stat };
  });

  return (
    <div className="p-4 md:p-6 lg:p-8 page-enter">
      {/* Onboarding Wizard for new users */}
      {showOnboarding && (
        <OnboardingWizard onComplete={() => {
          localStorage.removeItem('zouqai_needs_onboarding');
          setShowOnboarding(false);
          fetchData();
        }} />
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Welcome + Seed */}
        <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap" data-testid="dashboard-page">
          <div className="min-w-0">
            <h1 className="font-bold text-xl sm:text-2xl md:text-3xl text-foreground truncate" style={{ fontFamily: 'Tajawal, sans-serif' }} data-testid="dashboard-title">
              {t('modules.commandCenter')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('modules.welcomeMsg', { name: user?.name || '' })}
            </p>
          </div>
          {branches.length === 0 && (
            <Button onClick={handleSeed} disabled={seeding} data-testid="seed-data-btn" className="rounded-full">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
              {t('dashboard.sampleDataBtn')}
            </Button>
          )}
        </div>

        {/* AI Welcome Message */}
        <AIWelcomeMessage />

        {/* Module Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-testid="module-cards">
          {moduleCards.map(mod => {
            const Icon = mod.icon;
            const targetPath = mod.fullscreen ? mod.path : (mod.defaultPath || mod.path);
            const showBadge = mod.hasAlerts && alertCount > 0;
            return (
              <Link key={mod.key} to={targetPath} data-testid={`module-card-${mod.key}`}
                className="group relative block">
                <Card className="h-full border border-border/50 bg-card hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 rounded-xl overflow-hidden cursor-pointer">
                  <CardContent className="p-4 flex flex-col items-center text-center gap-2.5">
                    <div className={`w-12 h-12 rounded-xl ${mod.bgColor} flex items-center justify-center transition-transform group-hover:scale-110 duration-300`}>
                      <Icon className={`w-6 h-6 ${mod.textColor}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-xs sm:text-sm text-foreground leading-tight">{t(mod.labelKey)}</p>
                      {mod.stat && <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5">{mod.stat}</p>}
                    </div>
                    {showBadge && (
                      <span className="absolute top-2 end-2 min-w-[20px] h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1.5" data-testid="module-alert-badge">
                        {alertCount}
                      </span>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Smart Notifications Panel */}
        {smartNotifs && smartNotifs.total_count > 0 && (
          <Card className="border border-border/50 rounded-xl overflow-hidden" data-testid="smart-notifications-panel">
            <CardContent className="p-0">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/30">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500/10 to-orange-500/10">
                    <Zap className="h-4.5 w-4.5 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-foreground">{t('dashboard.smartAlerts')}</h3>
                    <p className="text-[11px] text-muted-foreground">{t('dashboard.smartAlertsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {smartNotifs.danger_count > 0 && (
                    <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px] font-semibold">
                      {t('dashboard.dangerCount', { count: smartNotifs.danger_count })}
                    </Badge>
                  )}
                  {smartNotifs.warning_count > 0 && (
                    <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-[10px] font-semibold">
                      {t('dashboard.warningCount', { count: smartNotifs.warning_count })}
                    </Badge>
                  )}
                </div>
              </div>
              {/* Notification Items */}
              <div className="divide-y divide-border/20">
                {smartNotifs.notifications.map((notif) => {
                  const iconMap = { 'package-x': XCircle, 'alert-triangle': AlertTriangle, 'clock': Clock, 'chef-hat': ChefHat, 'trending-down': TrendingDown, 'tag': Tag };
                  const colorMap = { red: 'bg-red-500/10 text-red-500', amber: 'bg-amber-500/10 text-amber-500', yellow: 'bg-yellow-500/10 text-yellow-500', orange: 'bg-orange-500/10 text-orange-500', blue: 'bg-blue-500/10 text-blue-500' };
                  const borderMap = { danger: 'border-s-red-500', warning: 'border-s-amber-500', info: 'border-s-blue-500' };
                  const NIcon = iconMap[notif.icon] || AlertTriangle;
                  const colorClass = colorMap[notif.color] || 'bg-muted/10 text-muted-foreground';
                  return (
                    <div key={notif.id} className={`flex items-center gap-3.5 px-5 py-3 border-s-[3px] ${borderMap[notif.type] || 'border-s-transparent'} hover:bg-muted/30 transition-colors`} data-testid={`smart-notif-${notif.id}`}>
                      <div className={`p-2 rounded-lg flex-shrink-0 ${colorClass}`}>
                        <NIcon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{lang === 'ar' ? notif.title : notif.title_en}</p>
                        {notif.items && (
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {notif.items.slice(0, 3).map((item, idx) => (
                              <span key={idx} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground">
                                {item.name} {item.stock !== undefined && `(${item.stock})`}
                              </span>
                            ))}
                            {notif.items.length > 3 && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground">+{notif.items.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <Link to={notif.action_path}>
                        <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1 text-primary hover:text-primary flex-shrink-0" data-testid={`smart-notif-action-${notif.id}`}>
                          {t('dashboard.viewSection')} <ArrowRight className="h-3 w-3" />
                        </Button>
                      </Link>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Analytics Summary */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : analytics && (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: t('dashboard.totalOrders'), value: analytics.total_orders, icon: ShoppingCart, color: 'text-primary' },
                { label: t('dashboard.revenue'), value: `${analytics.total_revenue} ${t('common.sar')}`, icon: DollarSign, color: 'text-emerald-500' },
                { label: t('dashboard.avgOrder'), value: `${analytics.avg_order_value} ${t('common.sar')}`, icon: TrendingUp, color: 'text-blue-500' },
                { label: t('dashboard.branches'), value: analytics.branch_count || branches.length, icon: Building2, color: 'text-purple-500' },
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <Card key={i} className="glass-stat rounded-xl overflow-hidden border border-border/50 hover:border-primary/20 transition-colors" data-testid={`stat-card-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                      <p className="text-xl sm:text-2xl font-bold text-foreground stat-number">{stat.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Inventory Alerts */}
            {inventoryAlerts.length > 0 && (
              <Card className="border border-amber-500/30 bg-amber-500/5 rounded-xl overflow-hidden" data-testid="dashboard-inventory-alerts">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-amber-500/10">
                        <Package className="h-4 w-4 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm">{t('dashboard.inventoryAlerts')}</h3>
                        <p className="text-[11px] text-muted-foreground">{inventoryAlerts.length} {t('dashboard.itemsNeedAttention')}</p>
                      </div>
                    </div>
                    <Link to="/dashboard/operations/inventory" data-testid="dashboard-view-inventory-btn">
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1 border-amber-500/30 text-amber-600 hover:bg-amber-500/10">
                        {t('dashboard.viewInventory')} <ArrowRight className="h-3 w-3" />
                      </Button>
                    </Link>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {inventoryAlerts.map(a => (
                      <div key={a.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${a.type === 'out_of_stock' ? 'bg-red-500/10 text-red-600 border border-red-500/20' : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'}`}
                        data-testid={`dashboard-inv-alert-${a.id}`}>
                        {a.type === 'out_of_stock' ? <XCircle className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                        <span>{a.name}</span>
                        <span className="opacity-60">({t('dashboard.stockLevel')}: {a.stock})</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2 bg-card border border-border/50 rounded-xl" data-testid="revenue-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> {t('dashboard.dailyRevenue')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {advanced?.daily_sales?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={advanced.daily_sales}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                        <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                        <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                        <Area type="monotone" dataKey="revenue" fill="hsl(var(--primary))" fillOpacity={0.15} stroke="hsl(var(--primary))" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">{t('dashboard.noDataChart')}</div>}
                </CardContent>
              </Card>

              <Card className="bg-card border border-border/50 rounded-xl" data-testid="top-items-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Trophy className="w-4 h-4" /> {t('dashboard.topItems')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {advanced?.top_items?.length > 0 ? (
                    <div className="space-y-2">
                      {advanced.top_items.slice(0, 6).map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}</span>
                          <div className="flex-1 h-6 rounded-full overflow-hidden bg-muted/50">
                            <div className="h-full rounded-full flex items-center px-2" style={{
                              width: `${Math.max(20, (item.quantity / (advanced.top_items[0]?.quantity || 1)) * 100)}%`,
                              backgroundColor: COLORS[i % COLORS.length] + '30',
                              borderRight: `3px solid ${COLORS[i % COLORS.length]}`
                            }}>
                              <span className="text-[10px] font-semibold text-foreground truncate">{item.name}</span>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-foreground min-w-[24px]">{item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  ) : <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">{t('dashboard.noDataShort')}</div>}
                </CardContent>
              </Card>
            </div>

            {/* Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="bg-card border border-border/50 rounded-xl" data-testid="peak-hours-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Flame className="w-4 h-4" /> {t('dashboard.peakHours')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {advanced?.peak_hours?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={advanced.peak_hours}>
                        <XAxis dataKey="hour" tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" />
                        <YAxis tick={{ fontSize: 9 }} stroke="hsl(var(--muted-foreground))" />
                        <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }} />
                        <Bar dataKey="orders" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">{t('dashboard.noDataShort')}</div>}
                </CardContent>
              </Card>

              <Card className="bg-card border border-border/50 rounded-xl" data-testid="status-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <PieIcon className="w-4 h-4" /> {t('dashboard.statusDist')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {advanced?.status_distribution?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={advanced.status_distribution.map(s => ({ ...s, name: STATUS_LABELS[s.status] || s.status }))}
                          cx="50%" cy="50%" outerRadius={65} dataKey="count" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false} style={{ fontSize: 9 }}>
                          {advanced.status_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">{t('dashboard.noDataShort')}</div>}
                </CardContent>
              </Card>

              <Card className="bg-card border border-border/50 rounded-xl" data-testid="recent-orders-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">
                    <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> {t('dashboard.recentOrders')}</span>
                    <Link to="/dashboard/operations/orders">
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1 text-primary hover:text-primary">
                        {t('common.viewAll')} <ArrowRight className="w-3 h-3" />
                      </Button>
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentOrders.length > 0 ? (
                    <div className="space-y-2">
                      {recentOrders.map((order, i) => (
                        <div key={order.id || i} className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0" data-testid={`recent-order-${i}`}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              order.status === 'delivered' ? 'bg-emerald-500' :
                              order.status === 'preparing' ? 'bg-orange-500' :
                              order.status === 'pending' ? 'bg-yellow-500' :
                              order.status === 'cancelled' ? 'bg-red-500' : 'bg-blue-500'
                            }`} />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-foreground truncate">{order.customer_name}</p>
                              <p className="text-[10px] text-muted-foreground">{order.items?.length || 0} {t('fleet.items')}</p>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-foreground flex-shrink-0">{order.total_price} {t('common.sar')}</span>
                        </div>
                      ))}
                    </div>
                  ) : <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">{t('dashboard.noDataShort')}</div>}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
